"use client";

import Image from "next/image";
import { useMemo, useState } from "react";
import {
  Anchor,
  Building2,
  Car,
  Dumbbell,
  Fuel,
  MapPin,
  Minus,
  Plus,
  ShipWheel,
  ShoppingBag,
  Store,
  Utensils,
} from "lucide-react";

import { cn } from "@louez/utils";

type PlaceCategory = "port" | "food" | "sport" | "shopping" | "services";

const categories: Array<{ id: "all" | PlaceCategory; label: string; color: string }> = [
  { id: "all", label: "Todo", color: "bg-white text-slate-950" },
  { id: "port", label: "Puerto", color: "bg-orange-500 text-white" },
  { id: "food", label: "Restauración", color: "bg-yellow-400 text-slate-950" },
  { id: "sport", label: "Deporte", color: "bg-emerald-600 text-white" },
  { id: "shopping", label: "Comercios", color: "bg-lime-500 text-slate-950" },
  { id: "services", label: "Servicios", color: "bg-sky-500 text-white" },
];

const categoryStyle: Record<PlaceCategory, { pin: string; soft: string }> = {
  port: { pin: "bg-orange-500", soft: "bg-orange-50 text-orange-700" },
  food: { pin: "bg-yellow-400", soft: "bg-yellow-50 text-yellow-800" },
  sport: { pin: "bg-emerald-600", soft: "bg-emerald-50 text-emerald-700" },
  shopping: { pin: "bg-lime-500", soft: "bg-lime-50 text-lime-800" },
  services: { pin: "bg-sky-500", soft: "bg-sky-50 text-sky-700" },
};

const places = [
  { id: "varador", name: "Varadero y marina", category: "port", x: 18, y: 27, icon: ShipWheel, description: "Mantenimiento, izado y servicios técnicos para tu embarcación." },
  { id: "deporte", name: "Zona deportiva", category: "sport", x: 37, y: 9, icon: Dumbbell, description: "Pumptrack, basket 3x3, calistenia, gimnasio y actividades al aire libre." },
  { id: "restauracion", name: "Restauración", category: "food", x: 49, y: 27, icon: Utensils, description: "Terrazas, restaurantes y sabores mediterráneos frente al puerto." },
  { id: "comercios", name: "Parque comercial", category: "shopping", x: 66, y: 28, icon: ShoppingBag, description: "Alimentación, hogar, moda y comercios para resolverlo todo en una visita." },
  { id: "parking", name: "Parking 24 h", category: "port", x: 78, y: 17, icon: Car, description: "Acceso directo al paseo, las tiendas y los pantalanes." },
  { id: "capitania", name: "Capitanía y servicios", category: "port", x: 72, y: 46, icon: Building2, description: "Información, atención al navegante, seguridad y servicios portuarios." },
  { id: "nautica-alvarez", name: "Náutica Álvarez", category: "services", x: 88, y: 42, icon: Store, description: "Alquiler de embarcaciones y servicios náuticos especializados." },
  { id: "gasolinera", name: "Gasolinera náutica", category: "port", x: 66, y: 53, icon: Fuel, description: "Repostaje junto al agua con acceso sencillo desde los pantalanes." },
  { id: "pantalanes", name: "Pantalanes", category: "port", x: 51, y: 76, icon: Anchor, description: "El corazón náutico del puerto y punto de salida al Mediterráneo." },
] as const satisfies ReadonlyArray<{
  id: string;
  name: string;
  category: PlaceCategory;
  x: number;
  y: number;
  icon: typeof MapPin;
  description: string;
}>;

export const PortExplore = () => {
  const [category, setCategory] = useState<"all" | PlaceCategory>("all");
  const [selectedId, setSelectedId] = useState<string>("restauracion");
  const [zoom, setZoom] = useState(1);

  const visiblePlaces = useMemo(
    () => places.filter((place) => category === "all" || place.category === category),
    [category],
  );
  const selected = places.find((place) => place.id === selectedId) ?? visiblePlaces[0] ?? places[0];
  const SelectedIcon = selected.icon;

  const selectCategory = (nextCategory: "all" | PlaceCategory) => {
    setCategory(nextCategory);
    const firstVisible = places.find(
      (place) => nextCategory === "all" || place.category === nextCategory,
    );
    if (firstVisible) setSelectedId(firstVisible.id);
  };

  return (
    <section id="puerto" className="bg-slate-50 py-14 md:py-18">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-7 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-sky-700 uppercase">Descubre cada rincón</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">Explora el puerto</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Navega por el plano real del Port de Premià y encuentra en segundos todo lo que necesitas.
            </p>
          </div>

          <div className="flex flex-wrap gap-2" aria-label="Filtrar lugares del puerto">
            {categories.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => selectCategory(item.id)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-semibold transition-all",
                  category === item.id
                    ? cn(item.color, "border-transparent shadow-sm")
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-950",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-sky-950 shadow-[0_30px_80px_-38px_rgba(8,47,73,0.8)] lg:grid lg:grid-cols-[minmax(0,1fr)_19rem]">
          <div className="relative overflow-hidden bg-[#0b4e66]">
            <div className="absolute top-4 right-4 z-30 flex overflow-hidden rounded-full border border-white/30 bg-sky-950/85 text-white shadow-lg backdrop-blur">
              <button type="button" aria-label="Alejar mapa" onClick={() => setZoom(1)} disabled={zoom === 1} className="flex size-10 items-center justify-center transition hover:bg-white/10 disabled:opacity-40">
                <Minus className="size-4" />
              </button>
              <span className="my-2 w-px bg-white/20" />
              <button type="button" aria-label="Acercar mapa" onClick={() => setZoom(1.22)} disabled={zoom > 1} className="flex size-10 items-center justify-center transition hover:bg-white/10 disabled:opacity-40">
                <Plus className="size-4" />
              </button>
            </div>

            <div className="relative aspect-[4/3] min-h-[25rem] w-full overflow-hidden lg:min-h-[35rem]">
              <div className="absolute inset-0 transition-transform duration-500 ease-out" style={{ transform: `scale(${zoom})` }}>
                <Image src="/images/port-premia-map.png" alt="Plano interactivo del Port de Premià" fill sizes="(max-width: 1024px) 100vw, 900px" className="object-fill" />

                {places.map((place) => {
                  const Icon = place.icon;
                  const visible = category === "all" || category === place.category;
                  const active = selected.id === place.id;
                  return (
                    <button
                      key={place.id}
                      type="button"
                      aria-label={`Mostrar ${place.name}`}
                      onClick={() => setSelectedId(place.id)}
                      className={cn(
                        "group absolute z-20 -translate-x-1/2 -translate-y-1/2 transition-all duration-300",
                        visible ? "scale-100 opacity-100" : "pointer-events-none scale-75 opacity-0",
                      )}
                      style={{ left: `${place.x}%`, top: `${place.y}%` }}
                    >
                      {active && <span className="absolute inset-0 animate-ping rounded-full bg-white/80" />}
                      <span className={cn(
                        "relative flex size-9 items-center justify-center rounded-full border-[3px] border-white text-white shadow-[0_6px_18px_rgba(15,23,42,0.45)] transition-transform group-hover:-translate-y-1 md:size-11",
                        categoryStyle[place.category].pin,
                        active && "-translate-y-1 scale-110 ring-4 ring-white/35",
                      )}>
                        <Icon className="size-4 md:size-5" />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="absolute right-4 bottom-4 left-4 z-30 rounded-2xl border border-white/50 bg-white/95 p-4 shadow-xl backdrop-blur lg:hidden">
              <div className="flex items-start gap-3">
                <span className={cn("flex size-10 shrink-0 items-center justify-center rounded-xl", categoryStyle[selected.category].soft)}>
                  <SelectedIcon className="size-5" />
                </span>
                <div>
                  <p className="font-bold text-slate-950">{selected.name}</p>
                  <p className="mt-1 text-xs leading-5 text-slate-600">{selected.description}</p>
                </div>
              </div>
            </div>
          </div>

          <aside className="hidden bg-white p-5 lg:flex lg:flex-col">
            <div>
              <p className="text-xs font-bold tracking-[0.16em] text-sky-700 uppercase">Punto seleccionado</p>
              <span className={cn("mt-4 flex size-11 items-center justify-center rounded-xl", categoryStyle[selected.category].soft)}>
                <SelectedIcon className="size-5" />
              </span>
              <h3 className="mt-4 text-xl font-bold text-slate-950">{selected.name}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{selected.description}</p>
            </div>

            <div className="my-5 h-px bg-slate-100" />
            <p className="mb-2 text-xs font-bold tracking-[0.12em] text-slate-400 uppercase">Lugares en esta categoría</p>
            <div className="space-y-1.5">
              {visiblePlaces.map((place) => {
                const Icon = place.icon;
                return (
                  <button
                    key={place.id}
                    type="button"
                    onClick={() => setSelectedId(place.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition",
                      selected.id === place.id ? "bg-sky-950 font-semibold text-white" : "text-slate-600 hover:bg-slate-50 hover:text-slate-950",
                    )}
                  >
                    <Icon className="size-4 shrink-0" />
                    <span className="truncate">{place.name}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-auto flex items-center gap-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
              <MapPin className="size-4 text-sky-700" /> Plano oficial · Marina Port Premià
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};
